// src/components/Logos.js
import React, { useState, useEffect, useRef } from 'react';
import { apiService } from './services/api'; 
import { API_BASE_URL } from './services/api/config'; 
import './style/Logos.css'; 

const Logos = () => {
  const [logos, setLogos] = useState([]);
  const [organisations, setOrganisations] = useState([]); 
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal & Upload State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'bulk'
  const [selectedOrganisationId, setSelectedOrganisationId] = useState(''); 
  const [filterOrganisationId, setFilterOrganisationId] = useState(''); 
  const [isUploading, setIsUploading] = useState(false);
  const [uploadText, setUploadText] = useState(''); 

  const [orgSearchInput, setOrgSearchInput] = useState('');
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null); 

  const fetchData = async () => {
    try {
      const [logosData, orgsData] = await Promise.all([
        apiService.getLogos(),
        apiService.getOrganisations() 
      ]);

      // Sort organisations alphabetically by name
      const sortedOrgs = orgsData.sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      setLogos(logosData);
      setOrganisations(sortedOrgs); // Save the sorted list
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // --- Step 1: Open Modal to select Organisation ---
  const handleOpenModal = (mode) => {
    setUploadMode(mode);
    setSelectedOrganisationId('');
    setIsModalOpen(true);
  };

  // --- Step 2: Proceed to File Picker ---
  const handleProceedToFiles = () => {
    if (!selectedOrganisationId) return;
    
    if (uploadMode === 'single') {
      fileInputRef.current.click();
    } else {
      bulkFileInputRef.current.click();
    }
  };

  // --- Step 3: Handle File Selection & Auto-Upload ---
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length === 0) {
      alert('No valid images found. Please select image files only.');
      event.target.value = null;
      return;
    }

    // Close modal and start uploading
    setIsModalOpen(false);
    setIsUploading(true);
    let successCount = 0;

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadText(`Uploading ${i + 1} of ${validFiles.length}...`);
      
      try {
        await processAndUploadFile(file, selectedOrganisationId);
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }

    setIsUploading(false);
    setUploadText('');
    event.target.value = null; // Reset input
    await fetchData(); // Refresh logos

    if (successCount < validFiles.length) {
      alert(`Uploaded ${successCount} of ${validFiles.length} logos successfully. Some failed.`);
    }
  };

  const processAndUploadFile = async (file, orgId) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    await new Promise((resolve) => { img.onload = resolve; });
    
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(img.width, img.height);
    const ratio = `${img.width / divisor}:${img.height / divisor}`;

    await apiService.uploadLogo(file, file.name, ratio, orgId);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;
    try {
      await apiService.deleteLogo(id);
      await fetchData(); 
    } catch (error) {
      console.error('Error deleting logo:', error);
    }
  };

  const handleDownload = async (logo) => {
    const url = getImageUrl(logo.id);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = logo.name || `logo-${logo.id}`; 
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const getImageUrl = (logoId) => {
    const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
    return `${baseUrl}/logos/${logoId}/image`;
  };

  const filteredLogos = logos.filter(logo => {
    // 1. Check search query
    const matchesSearch = logo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (logo.organisation_name && logo.organisation_name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // 2. Check organisation filter
    const matchesOrg = filterOrganisationId === '' || logo.organisation_id === parseInt(filterOrganisationId);
    
    return matchesSearch && matchesOrg;
  });
  return (
    <div className="logos-container">
      <div className="logos-header-section">
        <h2>Logo Library</h2>
        
        <div className="logos-toolbar">
          {/* --- UPDATED SEARCH & FILTER ROW --- */}
         <div className="filters-wrapper">
            <input 
              type="text" 
              className="logo-search-input small-search" 
              placeholder="Search logos..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            {/* --- NEW SEARCHABLE ORGANISATION DROPDOWN --- */}
            <div className="custom-dropdown" ref={dropdownRef}>
              <input
                type="text"
                className="logo-filter-input"
                placeholder="All Organisations"
                value={orgSearchInput}
                onChange={(e) => {
                  setOrgSearchInput(e.target.value);
                  setIsOrgDropdownOpen(true);
                  if (e.target.value === '') {
                    setFilterOrganisationId(''); // Reset filter if cleared
                  }
                }}
                onFocus={() => setIsOrgDropdownOpen(true)}
              />
              
              {isOrgDropdownOpen && (
                <ul className="custom-dropdown-list">
                  <li onClick={() => {
                    setFilterOrganisationId('');
                    setOrgSearchInput('');
                    setIsOrgDropdownOpen(false);
                  }}>
                    All Organisations
                  </li>
                  
                  {organisations
                    // Filter the list so it only shows orgs starting with the typed letters
                    .filter(org => org.name.toLowerCase().startsWith(orgSearchInput.toLowerCase()))
                    .map(org => (
                      <li 
                        key={org.id} 
                        onClick={() => {
                          setFilterOrganisationId(org.id);
                          setOrgSearchInput(org.name); // Fill input with selected name
                          setIsOrgDropdownOpen(false); // Close dropdown
                        }}
                      >
                        {org.name}
                      </li>
                    ))
                  }
                </ul>
              )}
            </div>
          </div>
          
          <div className="header-actions">
            <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelect} />
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={bulkFileInputRef} onChange={handleFileSelect} />

            <button className="logo-btn-outline" onClick={() => handleOpenModal('bulk')} disabled={isUploading}>
              {isUploading && uploadMode === 'bulk' ? uploadText : 'Bulk Upload'}
            </button>
            <button className="logo-btn-primary" onClick={() => handleOpenModal('single')} disabled={isUploading}>
              {isUploading && uploadMode === 'single' ? uploadText : '+ Upload New Logo'}
            </button>
          </div>
        </div>
      </div>

      <div className="logos-grid">
        {filteredLogos.map((logo) => (
          <div key={logo.id} className="logo-card">
            <div className="logo-thumbnail">
              <img src={getImageUrl(logo.id)} alt={logo.name} />
            </div>
            <div className="logo-details">
              <h4 className="logo-name">{logo.name}</h4>
              <p className="logo-org">{logo.organisation_name || 'No Organisation'}</p>
              <div className="logo-meta">
                <span className="logo-format">{logo.format}</span>
                <span className="logo-ratio">{logo.ratio}</span>
              </div>
              <div className="logo-actions">
                <button className="logo-btn-secondary" onClick={() => handleDownload(logo)}>Download</button>
                <button className="logo-btn-danger" onClick={() => handleDelete(logo.id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* --- ORGANISATION SELECTION MODAL --- */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Select Organisation</h3>
            <p>Please select the organisation for the logo(s) you are about to upload.</p>
            
            <div className="form-group">
              <label>Organisation *</label>
              <select 
                value={selectedOrganisationId} 
                onChange={(e) => setSelectedOrganisationId(e.target.value)}
                className="logo-select"
              >
                <option value="">-- Choose an Organisation --</option>
                {organisations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="logo-btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button 
                className="logo-btn-primary" 
                onClick={handleProceedToFiles} 
                disabled={!selectedOrganisationId}
              >
                Choose File(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logos;
